import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import i18n from 'i18next';
import { auditApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Download, AlertTriangle, RefreshCw, Loader2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

const PAGE_SIZE = 50;

const ACTION_BADGE_STYLES = {
    LOGIN_SUCCESS: 'bg-[#D1FAE5] text-[#059669]',
    LOGIN_FAILED: 'bg-[#FEE2E2] text-[#DC2626]',
    ACCESS_DENIED: 'bg-[#FEE2E2] text-[#DC2626]',
    USER_ROLE_CHANGED: 'bg-[#DBEAFE] text-[#002FA7]',
    USER_STATUS_CHANGED: 'bg-[#FEF3C7] text-[#D97706]',
    USER_DELETED: 'bg-[#FEE2E2] text-[#DC2626]',
    USER_RESTORED: 'bg-[#D1FAE5] text-[#059669]',
    USER_PURGED: 'bg-[#7F1D1D]/10 text-[#7F1D1D]',
    CANDIDATE_DELETED: 'bg-[#FEF3C7] text-[#D97706]',
    JOB_DELETED: 'bg-[#FEF3C7] text-[#D97706]',
    PLAN_CHANGE_REQUESTED: 'bg-[#DBEAFE] text-[#002FA7]',
    PLAN_CHECKOUT_INITIATED: 'bg-[#E0F2FE] text-[#0284C7]',
    PLAN_CANCELED: 'bg-[#FEE2E2] text-[#DC2626]',
    RATE_LIMIT_BLOCKED: 'bg-[#FEF3C7] text-[#92400E]',
};

function ActionBadge({ action }) {
    const cls = ACTION_BADGE_STYLES[action] || 'bg-[#F3F4F6] text-[#4B5563]';
    return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`} data-testid={`audit-action-${action}`}>
            {action}
        </span>
    );
}

export default function AuditLogsPage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [actions, setActions] = useState([]);
    const [page, setPage] = useState(0);

    // Filters — initial values pulled from URL search params if present.
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState(searchParams.get('action') || 'all');
    const [targetType, setTargetType] = useState(searchParams.get('target_type') || 'all');
    const [actorEmail, setActorEmail] = useState('');
    const [orgId, setOrgId] = useState(searchParams.get('org_id') || '');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const TARGET_TYPES = useMemo(() => ([
        { value: 'all', label: t('audit.target_all') },
        { value: 'auth', label: t('audit.target_auth') },
        { value: 'user', label: t('audit.target_user') },
        { value: 'candidate', label: t('audit.target_candidate') },
        { value: 'job', label: t('audit.target_job') },
        { value: 'organization', label: t('audit.target_organization') },
        { value: 'subscription', label: t('audit.target_subscription') },
    ]), [t]);

    // Anomalies
    const [anomalies, setAnomalies] = useState({ rapid_deletes: [], role_escalations: [] });
    const [anomaliesLoading, setAnomaliesLoading] = useState(false);

    // Sort state (column header click)
    const [sort, setSort] = useState({ key: 'created_at', order: 'desc' });

    const toggleSort = (key) => {
        setPage(0);
        setSort((prev) => {
            if (prev.key !== key) return { key, order: 'desc' };
            if (prev.order === 'desc') return { key, order: 'asc' };
            return { key: 'created_at', order: 'desc' };
        });
    };

    const SortIcon = ({ column }) => {
        if (sort.key !== column) return <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />;
        return sort.order === 'desc'
            ? <ArrowDown className="w-3 h-3 inline ml-1 text-[#002FA7]" />
            : <ArrowUp className="w-3 h-3 inline ml-1 text-[#002FA7]" />;
    };

    const params = useMemo(() => {
        const p = {
            skip: page * PAGE_SIZE,
            limit: PAGE_SIZE,
            sort_by: sort.key,
            sort_order: sort.order,
        };
        if (search) p.search = search;
        if (actionFilter !== 'all') p.action = actionFilter;
        if (targetType !== 'all') p.target_type = targetType;
        if (actorEmail) p.actor_email = actorEmail;
        if (orgId) p.organization_id = orgId;
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo) p.date_to = dateTo;
        return p;
    }, [page, search, actionFilter, targetType, actorEmail, orgId, dateFrom, dateTo, sort]);

    const loadLogs = async () => {
        setLoading(true);
        try {
            const res = await auditApi.listLogs(params);
            setLogs(res.data.logs || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const loadActions = async () => {
        try {
            const res = await auditApi.listActions();
            setActions(res.data.actions || []);
        } catch {
            /* non-blocking */
        }
    };

    const loadAnomalies = async () => {
        setAnomaliesLoading(true);
        try {
            const res = await auditApi.detectAnomalies(24, 5);
            setAnomalies(res.data || { rapid_deletes: [], role_escalations: [] });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAnomaliesLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    useEffect(() => {
        loadActions();
        loadAnomalies();
    }, []);

    const resetFilters = () => {
        setSearch('');
        setActionFilter('all');
        setTargetType('all');
        setActorEmail('');
        setOrgId('');
        setDateFrom('');
        setDateTo('');
        setPage(0);
    };

    const handleExport = () => {
        const exportParams = { ...params };
        delete exportParams.skip;
        delete exportParams.limit;
        const url = auditApi.exportCsvUrl(exportParams);
        window.open(url, '_blank');
    };

    const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
    const anomalyCount = (anomalies.rapid_deletes?.length || 0) + (anomalies.role_escalations?.length || 0);

    return (
        <div className="space-y-6" data-testid="audit-logs-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{t('audit.page_title')}</h1>
                    <p className="text-sm text-[#9CA3AF] mt-1">
                        {t('audit.page_subtitle')}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={loadLogs} disabled={loading} data-testid="audit-refresh-btn" className="flex-1 sm:flex-initial">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        {t('common.refresh')}
                    </Button>
                    <Button onClick={handleExport} data-testid="audit-export-csv-btn" className="bg-[#002FA7] hover:bg-[#002480] text-white flex-1 sm:flex-initial">
                        <Download className="w-4 h-4 mr-2" />
                        {t('common.export')} CSV
                    </Button>
                </div>
            </div>

            {/* Anomaly detection banner */}
            {anomalyCount > 0 && (
                <div className="rounded-lg border border-[#F59E0B]/30 bg-[#FEF3C7] p-4" data-testid="audit-anomalies-banner">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-[#D97706] mt-0.5" />
                        <div className="flex-1">
                            <h3 className="font-semibold text-[#92400E] text-sm">
                                {anomalyCount === 1
                                    ? t('audit.anomalies_title_one', { count: anomalyCount })
                                    : t('audit.anomalies_title_other', { count: anomalyCount })}
                            </h3>
                            <ul className="mt-2 text-sm text-[#78350F] space-y-1">
                                {anomalies.rapid_deletes?.map((r, idx) => (
                                    <li key={`rd-${idx}`}>
                                        {t('audit.anomaly_rapid_deletes', { actor: r.actor_email || r.actor_id, count: r.count })}
                                    </li>
                                ))}
                                {anomalies.role_escalations?.map((r, idx) => (
                                    <li key={`re-${idx}`}>
                                        {t('audit.anomaly_role_escalation', { actor: r.actor_email, target: r.target_id?.slice(0, 12) })}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-3 text-[#9CA3AF]" />
                        <Input
                            placeholder={t('audit.search_placeholder')}
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            className="pl-10"
                            data-testid="audit-search-input"
                        />
                    </div>

                    <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
                        <SelectTrigger data-testid="audit-action-filter">
                            <SelectValue placeholder={t('audit.filter_action')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('audit.all_actions')}</SelectItem>
                            {actions.map(a => (
                                <SelectItem key={a} value={a}>{a}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={targetType} onValueChange={(v) => { setTargetType(v); setPage(0); }}>
                        <SelectTrigger data-testid="audit-target-filter">
                            <SelectValue placeholder={t('audit.filter_target')} />
                        </SelectTrigger>
                        <SelectContent>
                            {TARGET_TYPES.map(tt => (
                                <SelectItem key={tt.value} value={tt.value}>{tt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Input
                        placeholder={t('audit.filter_actor_email')}
                        value={actorEmail}
                        onChange={(e) => { setActorEmail(e.target.value); setPage(0); }}
                        data-testid="audit-actor-email-filter"
                    />
                    <Input
                        placeholder={t('audit.filter_organization')}
                        value={orgId}
                        onChange={(e) => { setOrgId(e.target.value); setPage(0); }}
                        data-testid="audit-org-id-filter"
                    />
                    <div className="flex gap-2">
                        <Input
                            type="datetime-local"
                            value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value ? `${e.target.value}:00` : ''); setPage(0); }}
                            data-testid="audit-date-from"
                            className="text-xs"
                            aria-label={t('audit.date_from')}
                            title={t('audit.date_from')}
                        />
                        <Input
                            type="datetime-local"
                            value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value ? `${e.target.value}:59` : ''); setPage(0); }}
                            data-testid="audit-date-to"
                            className="text-xs"
                            aria-label={t('audit.date_to')}
                            title={t('audit.date_to')}
                        />
                    </div>
                </div>
                <div className="flex justify-between items-center">
                    <p className="text-xs text-[#9CA3AF]">
                        {total <= 1 ? t('audit.entries_count_one', { count: total }) : t('audit.entries_count_other', { count: total.toLocaleString() })}
                    </p>
                    <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="audit-reset-filters">
                        {t('audit.reset_filters')}
                    </Button>
                </div>
            </div>

            {/* Logs table */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F9FAFB]">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase cursor-pointer select-none hover:bg-[#F3F4F6]"
                                    onClick={() => toggleSort('created_at')}
                                    data-testid="sort-created-at"
                                >
                                    {t('audit.col_date')} <SortIcon column="created_at" />
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase cursor-pointer select-none hover:bg-[#F3F4F6]"
                                    onClick={() => toggleSort('action')}
                                    data-testid="sort-action"
                                >
                                    {t('audit.col_action')} <SortIcon column="action" />
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase cursor-pointer select-none hover:bg-[#F3F4F6]"
                                    onClick={() => toggleSort('actor_email')}
                                    data-testid="sort-actor-email"
                                >
                                    {t('audit.col_actor')} <SortIcon column="actor_email" />
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase cursor-pointer select-none hover:bg-[#F3F4F6]"
                                    onClick={() => toggleSort('target_type')}
                                    data-testid="sort-target-type"
                                >
                                    {t('audit.col_target')} <SortIcon column="target_type" />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('audit.col_target_org')}</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('audit.col_ip')}</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('audit.col_details')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                            {loading ? (
                                <tr><td colSpan={7} className="text-center py-12">
                                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#9CA3AF]" />
                                </td></tr>
                            ) : logs.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-12 text-[#9CA3AF]" data-testid="audit-no-logs">
                                    {t('audit.no_entries')}
                                </td></tr>
                            ) : logs.map((log) => (
                                <tr key={log.id} className="hover:bg-[#F9FAFB]">
                                    <td className="px-4 py-3 text-[#9CA3AF] text-xs whitespace-nowrap">
                                        {log.created_at ? new Date(log.created_at).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'fr-FR') : '-'}
                                    </td>
                                    <td className="px-4 py-3"><ActionBadge action={log.action} /></td>
                                    <td className="px-4 py-3 text-[#0A0A0A]">
                                        <div className="font-medium">{log.actor_email || '—'}</div>
                                        {log.actor_role && (
                                            <div className="text-xs text-[#9CA3AF]">{log.actor_role}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-[#4B5563]">
                                        <div>{log.target_type || '—'}</div>
                                        {log.target_id && (
                                            <div className="text-xs text-[#9CA3AF] font-mono">{log.target_id.slice(0, 18)}...</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-[#4B5563]">
                                        {log.target_organization_name || log.organization_name || (log.target_organization_id ? log.target_organization_id.slice(0, 10) + '...' : (log.organization_id ? log.organization_id.slice(0, 10) + '...' : '—'))}
                                    </td>
                                    <td className="px-4 py-3 text-[#9CA3AF] text-xs font-mono">{log.ip_address || '—'}</td>
                                    <td className="px-4 py-3">
                                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                                            <details className="cursor-pointer">
                                                <summary className="text-[#002FA7] text-xs hover:underline">{t('audit.view_details')}</summary>
                                                <pre className="mt-2 text-xs bg-[#F9FAFB] p-2 rounded max-w-xs overflow-x-auto">
                                                    {JSON.stringify(log.metadata, null, 2)}
                                                </pre>
                                            </details>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-[#E5E7EB] px-4 py-3" data-testid="audit-pagination">
                    <p className="text-xs text-[#9CA3AF]">
                        {total === 0
                            ? 'Aucune entrée'
                            : `Affichage ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} sur ${total.toLocaleString()}`}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(0)}
                            disabled={page === 0}
                            data-testid="audit-first-page"
                        >
                            «
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="audit-prev-page"
                        >
                            Précédent
                        </Button>
                        <span className="text-xs text-[#4B5563] px-2">
                            Page {page + 1} / {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => p + 1)}
                            disabled={(page + 1) * PAGE_SIZE >= total}
                            data-testid="audit-next-page"
                        >
                            Suivant
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(totalPages - 1)}
                            disabled={(page + 1) * PAGE_SIZE >= total}
                            data-testid="audit-last-page"
                        >
                            »
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
